import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { scratchpadAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { SUBJECTS, ORGAN_SYSTEMS, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
    Plus, Trash2, Edit, Users, StickyNote, Copy, LogIn, ChevronRight, Tag, Clock, Search, X, ArrowRight
} from 'lucide-react';

export default function ScratchPads() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [owned, setOwned] = useState([]);
    const [shared, setShared] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showJoinDialog, setShowJoinDialog] = useState(false);
    const [joinCode, setJoinCode] = useState('');
    const [joining, setJoining] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: '', description: '', tags: [] });
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const searchTimeout = React.useRef(null);

    useEffect(() => { fetchPads(); }, []);

    const fetchPads = async () => {
        try {
            setLoading(true);
            const res = await scratchpadAPI.list();
            setOwned(res.data.owned || []);
            setShared(res.data.shared || []);
        } catch (err) {
            console.error('Failed to fetch scratch pads:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!value.trim()) { setSearchResults(null); return; }
        searchTimeout.current = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await scratchpadAPI.search(value.trim());
                setSearchResults(res.data);
            } catch (err) {
                console.error('Search failed:', err);
            } finally {
                setSearching(false);
            }
        }, 300);
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchResults(null);
    };

    const highlightMatch = (text, query) => {
        if (!query || !text) return text;
        const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === query.toLowerCase()
                ? <mark key={i} className="bg-amber-200 text-amber-900 rounded px-0.5">{part}</mark>
                : part
        );
    };

    const handleCreate = async () => {
        if (!form.name.trim()) { toast.error('Please enter a name'); return; }
        setCreating(true);
        try {
            const res = await scratchpadAPI.create(form);
            toast.success('Scratch pad created!');
            setShowCreateDialog(false);
            setForm({ name: '', description: '', tags: [] });
            navigate(`/scratchpad/${res.data.id}`);
        } catch (err) {
            toast.error('Failed to create scratch pad');
        } finally {
            setCreating(false);
        }
    };

    const handleJoin = async () => {
        if (!joinCode.trim()) { toast.error('Please enter a code'); return; }
        setJoining(true);
        try {
            const res = await scratchpadAPI.join(joinCode.trim().toUpperCase());
            toast.success(res.data.message);
            setShowJoinDialog(false);
            setJoinCode('');
            navigate(`/scratchpad/${res.data.scratchpad_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Invalid code');
        } finally {
            setJoining(false);
        }
    };

    const handleDelete = async (padId) => {
        try {
            await scratchpadAPI.delete(padId);
            toast.success('Scratch pad deleted');
            fetchPads();
        } catch (err) {
            toast.error('Failed to delete');
        }
    };

    const toggleTag = (tag) => {
        setForm(prev => ({
            ...prev,
            tags: prev.tags.includes(tag)
                ? prev.tags.filter(t => t !== tag)
                : [...prev.tags, tag]
        }));
    };

    const PadCard = ({ pad, isOwned }) => (
        <Card
            className={cn(
                "group cursor-pointer hover:shadow-lg transition-all hover:scale-[1.01]",
                isOwned ? "border-slate-200" : "border-teal-200 bg-teal-50/30"
            )}
            onClick={() => navigate(`/scratchpad/${pad.id}`)}
            data-testid={`pad-card-${pad.id}`}
        >
            <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900 truncate text-base" style={{ fontFamily: 'Manrope' }}>
                            {pad.name}
                        </h3>
                        {pad.description && (
                            <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{pad.description}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <Badge variant="outline" className={cn("text-xs", isOwned ? "border-indigo-200 text-indigo-600" : "border-teal-200 text-teal-600")}>
                            {isOwned ? 'Owner' : pad.role === 'edit' ? 'Editor' : 'Viewer'}
                        </Badge>
                    </div>
                </div>

                {pad.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                        {pad.tags.slice(0, 4).map(t => (
                            <Badge key={t} variant="outline" className="text-xs border-slate-200 text-slate-500">
                                {t}
                            </Badge>
                        ))}
                        {pad.tags.length > 4 && (
                            <Badge variant="outline" className="text-xs border-slate-200 text-slate-400">+{pad.tags.length - 4}</Badge>
                        )}
                    </div>
                )}

                <div className="flex items-center justify-between text-xs text-slate-400">
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                            <StickyNote className="h-3 w-3" /> {pad.note_count || 0} notes
                        </span>
                        <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" /> {(pad.member_count || 0) + 1}
                        </span>
                    </div>
                    {!isOwned && (
                        <span className="text-teal-500">by {pad.owner_name}</span>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                </div>

                {isOwned && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                            <Copy className="h-3 w-3" />
                            <span className="font-mono tracking-wider">{pad.share_code}</span>
                        </div>
                        <Button
                            size="sm" variant="ghost"
                            className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                            onClick={(e) => { e.stopPropagation(); handleDelete(pad.id); }}
                            data-testid={`delete-pad-${pad.id}`}
                        >
                            <Trash2 className="h-3 w-3 mr-1" /> Delete
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-8 w-48" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-40" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="scratchpads-container">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <p className="text-slate-500">Collaborative notes — jot down key concepts, share with classmates</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowJoinDialog(true)} data-testid="join-pad-btn">
                        <LogIn className="mr-2 h-4 w-4" /> Join with Code
                    </Button>
                    <Button onClick={() => { setForm({ name: '', description: '', tags: [] }); setShowCreateDialog(true); }} data-testid="create-pad-btn">
                        <Plus className="mr-2 h-4 w-4" /> New Scratch Pad
                    </Button>
                </div>
            </div>

                {/* Search Bar */}
                <div className="relative" data-testid="search-section">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search across all your scratch pads..."
                            value={searchQuery}
                            onChange={e => handleSearch(e.target.value)}
                            className="pl-10 pr-10"
                            data-testid="search-input"
                        />
                        {searchQuery && (
                            <button
                                onClick={clearSearch}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                data-testid="clear-search-btn"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>

                    {/* Search Results */}
                    {searchResults && (
                        <Card className="mt-3 border-amber-200 bg-amber-50/30" data-testid="search-results">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-medium text-slate-700">
                                        {searching ? 'Searching...' : (
                                            <>{searchResults.total} result{searchResults.total !== 1 ? 's' : ''} for "<span className="text-amber-700">{searchResults.query}</span>"</>
                                        )}
                                    </p>
                                    <Button variant="ghost" size="sm" onClick={clearSearch} className="h-7 text-xs text-slate-500">
                                        Clear
                                    </Button>
                                </div>
                                {searchResults.results?.length > 0 ? (
                                    <div className="space-y-2 max-h-80 overflow-y-auto">
                                        {searchResults.results.map(note => (
                                            <button
                                                key={note.id}
                                                onClick={() => navigate(`/scratchpad/${note.scratchpad_id}`)}
                                                className="w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg border border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm transition-all group"
                                                data-testid={`search-result-${note.id}`}
                                            >
                                                <StickyNote className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="text-sm font-medium text-slate-800 truncate">
                                                            {highlightMatch(note.title, searchQuery)}
                                                        </span>
                                                        <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-400 flex-shrink-0">
                                                            {note.category}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-xs text-slate-500 line-clamp-1">
                                                        {highlightMatch(note.body, searchQuery)}
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                                        in <span className="font-medium text-indigo-500">{note.pad_name}</span> &middot; by {note.created_by_name}
                                                    </p>
                                                </div>
                                                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-amber-500 flex-shrink-0 mt-0.5 transition-colors" />
                                            </button>
                                        ))}
                                    </div>
                                ) : !searching && (
                                    <p className="text-sm text-slate-500 text-center py-4">No matching notes found</p>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>

                <Tabs defaultValue="mine" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 max-w-xs">
                        <TabsTrigger value="mine">My Pads ({owned.length})</TabsTrigger>
                        <TabsTrigger value="shared">Shared ({shared.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="mine" className="mt-6">
                        {owned.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {owned.map(p => <PadCard key={p.id} pad={p} isOwned />)}
                            </div>
                        ) : (
                            <Card className="border-dashed border-2 border-slate-200">
                                <CardContent className="p-12 text-center">
                                    <StickyNote className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Scratch Pads Yet</h3>
                                    <p className="text-slate-500 mb-4">Create one to start jotting down key concepts</p>
                                    <Button onClick={() => setShowCreateDialog(true)} data-testid="create-first-pad-btn">
                                        <Plus className="mr-2 h-4 w-4" /> Create Scratch Pad
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    <TabsContent value="shared" className="mt-6">
                        {shared.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {shared.map(p => <PadCard key={p.id} pad={p} isOwned={false} />)}
                            </div>
                        ) : (
                            <Card className="border-dashed border-2 border-slate-200">
                                <CardContent className="p-12 text-center">
                                    <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Shared Pads</h3>
                                    <p className="text-slate-500 mb-4">Ask a classmate for a share code to join their scratch pad</p>
                                    <Button variant="outline" onClick={() => setShowJoinDialog(true)}>
                                        <LogIn className="mr-2 h-4 w-4" /> Join with Code
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Create Dialog */}
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle style={{ fontFamily: 'Manrope' }}>New Scratch Pad</DialogTitle>
                            <DialogDescription>Create a space for collaborative study notes</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="space-y-2">
                                <Label>Name</Label>
                                <Input
                                    placeholder="e.g., Cardiology High-Yields"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    data-testid="pad-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Description (optional)</Label>
                                <Input
                                    placeholder="Quick notes for our study group"
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                    data-testid="pad-desc-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Tags (optional)</Label>
                                <p className="text-xs text-slate-400">Select subjects or organ systems to categorize</p>
                                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                                    {[...SUBJECTS, ...ORGAN_SYSTEMS].map(tag => (
                                        <Badge
                                            key={tag}
                                            variant={form.tags.includes(tag) ? "default" : "outline"}
                                            className={cn("cursor-pointer text-xs transition-all", form.tags.includes(tag) && "bg-indigo-500")}
                                            onClick={() => toggleTag(tag)}
                                        >
                                            {tag}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                            <Button onClick={handleCreate} disabled={creating} data-testid="confirm-create-pad-btn">
                                {creating ? 'Creating...' : 'Create'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Join Dialog */}
                <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
                    <DialogContent className="max-w-sm">
                        <DialogHeader>
                            <DialogTitle style={{ fontFamily: 'Manrope' }}>Join Scratch Pad</DialogTitle>
                            <DialogDescription>Enter the 6-character share code</DialogDescription>
                        </DialogHeader>
                        <div className="py-2">
                            <Input
                                placeholder="e.g., ABC123"
                                value={joinCode}
                                onChange={e => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
                                className="text-center font-mono text-lg tracking-[0.3em] uppercase"
                                maxLength={6}
                                data-testid="join-code-input"
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowJoinDialog(false)}>Cancel</Button>
                            <Button onClick={handleJoin} disabled={joining || joinCode.length < 6} data-testid="confirm-join-btn">
                                {joining ? 'Joining...' : 'Join'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
    );
}
